/**
 * ==========================================================================
 *  FIGURE: the people, and what makes them read as people
 * ==========================================================================
 *  Everyone in the venue is put together from primitives, like the rest of
 *  the blockout. A capsule and a ball read as "a body" from thirty metres;
 *  from the metre away you hurdle a sleeper at, or the arm's length Hannu is
 *  at when he catches you, they read as a bin bag with a beach ball on it.
 *  What the eye wants at that range is a *face* - and a face is the one thing
 *  a primitive cannot do.
 *
 *  So it is drawn. The same way every other surface here is: onto a canvas
 *  at load, from a seeded generator, then wrapped round a sphere. The texture
 *  carries what is flat - eyes, brows, mouth, the hairline, stubble - and
 *  geometry carries what is not: a nose, a jaw, ears, and a shell of hair
 *  over the top and back that gives the silhouette something other than a
 *  ball. Neither half works alone. Painted hair on a smooth ball is a
 *  swimming cap; a hair shell over a blank ball is a helmet.
 *
 *  Heads are built here for all three kinds of figure `World` draws - the
 *  sleepers, the one man awake, and Hannu - so that a single recipe decides
 *  what a face is. Hannu's body is here too, because he is the only one who
 *  stands up and is looked at from every side. The sleepers' and the sitter's
 *  bodies stay in `World`, where their poses are.
 *
 *  Every head is built facing local +Z with its crown up, and it is the
 *  caller's job to lay it down or turn it round. The sphere's UVs put +Z at
 *  u = 0.25, which is why the canvas below is addressed in degrees of
 *  longitude from the front and latitude down from the crown, rather than in
 *  pixels: "the eyes are 20° either side of the nose, 88° down from the top"
 *  is a sentence about a face, and a pixel coordinate is not.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { drawFace, rgb } from './Face';
import { TUOPPI } from '../config/level';

export type Expression = 'asleep' | 'awake' | 'delighted' | 'angry' | 'sad';

/**
 * What is on the head. `capped` paints only what shows below a cap's rim and
 * builds no hair shell at all, and it is the caller who puts the cap on.
 */
export type HairStyle = 'short' | 'long' | 'receding' | 'bald' | 'buzz' | 'capped';

export interface HeadSpec {
  /** Skull radius before `SKULL_SCALE`. A grown man's is about 0.125 m. */
  radius: number;
  skin: number;
  hair: number;
  style: HairStyle;
  expression: Expression;
  /** Prebuild a blackout face so switching needs no canvas work. */
  blackoutFace?: boolean | 'sad';
  /** Flat skin, for the parts that stand off the face. Shared with the body. */
  skinMaterial: THREE.Material;
  hairMaterial: THREE.Material;
  /** Authoring randomness: the same seed draws the same face. */
  rng: () => number;
  moustache?: boolean;
  stubble?: boolean;
  /** Skin coming off in sheets. For the man from the palju and nobody else. */
  peeled?: boolean;
}

/**
 * A head is narrower than it is deep, and taller than either. The skull, the
 * hair shells and anything a caller hangs on the head - a cap - share this,
 * so they all fit the same egg.
 */
export const SKULL_SCALE = new THREE.Vector3(0.88, 1.06, 1.0);

export type Ctx = CanvasRenderingContext2D;
export type Rng = () => number;

export const W = 512;
export const H = 256;
/** Pixels per degree, the same along both axes: 360° across, 180° down. */
export const PX = W / 360;
/** Canvas x of a longitude, in degrees from straight ahead, positive to the figure's +X. */
export const px = (lon: number): number => (0.25 + lon / 360) * W;
/** Canvas y of a latitude, in degrees down from the crown. */
export const py = (lat: number): number => (lat / 180) * H;
export const deg = (n: number): number => n * PX;

export const IRISES = [0x4a2f1c, 0x3b5a7a, 0x5b6e58, 0x6b6f78, 0x2f2a26];

/** Weighted the way a hall of Finnish men is: mostly short, a few of everything else. */
export function pickHairStyle(rng: Rng): HairStyle {
  const styles: HairStyle[] = ['short', 'short', 'short', 'long', 'receding', 'bald', 'buzz'];
  return styles[(rng() * styles.length) | 0];
}

/* ---------------------------------------------------------------- head --- */

/**
 * One head, centred on the skull, facing +Z. The neck is the caller's.
 */
export function buildHead(spec: HeadSpec): THREE.Group {
  const r = spec.radius;
  const head = new THREE.Group();
  head.name = 'paa';

  const faceRolls: number[] = [];
  const faceSpec = spec.blackoutFace ? {
    ...spec,
    rng: () => {
      const value = spec.rng();
      faceRolls.push(value);
      return value;
    },
  } : spec;
  const skull = new THREE.Mesh(
    new THREE.SphereGeometry(r, 18, 12),
    new THREE.MeshLambertMaterial({ map: drawFace(faceSpec) }),
  );
  skull.name = 'kasvot';
  skull.scale.copy(SKULL_SCALE);
  head.add(skull);
  if (spec.blackoutFace) {
    // Reuse the face's rolls: hair, stubble and iris colour must stay the same.
    let roll = 0;
    const blackout = new THREE.Mesh(skull.geometry, new THREE.MeshLambertMaterial({
      map: drawFace({ ...spec, expression: spec.blackoutFace === 'sad' ? 'sad' : 'angry', rng: () => faceRolls[roll++] }),
    }));
    blackout.name = 'kasvot-sahkokatko';
    blackout.scale.copy(SKULL_SCALE);
    blackout.visible = false;
    head.add(blackout);
  }

  // A jaw, so the head is an egg with a chin and not a ball. It lives inside
  // the skull everywhere except where it counts, and it is flat skin rather
  // than the face texture because a second face is not what a chin needs.
  const jaw = new THREE.Mesh(new THREE.SphereGeometry(r * 0.8, 12, 8), spec.skinMaterial);
  jaw.scale.set(0.8, 0.95, 0.92);
  jaw.position.set(0, -r * 0.4, r * 0.06);
  head.add(jaw);

  // A four-sided cone, ridge up, tip a little below the base: the one
  // feature of a face that has to cast a shadow to be there.
  const nose = new THREE.Mesh(new THREE.ConeGeometry(r * 0.16, r * 0.42, 4), spec.skinMaterial);
  nose.rotation.set(Math.PI / 2 + 0.18, Math.PI / 4, 0);
  nose.position.set(0, -r * 0.1, r * 1.0);
  head.add(nose);

  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.SphereGeometry(r * 0.26, 8, 6), spec.skinMaterial);
    ear.scale.set(0.4, 1, 0.7);
    ear.position.set(side * r * 0.86, -r * 0.02, -r * 0.06);
    head.add(ear);
  }

  // The hair that has volume: a cap over the crown down to the hairline, and
  // a band round the back from there to the nape, leaving the face open. Each
  // style's painted hairline sits just inside its shell's edge, so the rim
  // reads as the fringe rather than as a gap.
  const shell = (radius: number, lonFrom: number, lonTo: number, latFrom: number, latTo: number) => {
    const rad = Math.PI / 180;
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(
        radius,
        16,
        8,
        Math.PI / 2 + lonFrom * rad,
        (lonTo - lonFrom) * rad,
        latFrom * rad,
        (latTo - latFrom) * rad,
      ),
      spec.hairMaterial,
    );
    mesh.scale.copy(SKULL_SCALE);
    head.add(mesh);
  };
  switch (spec.style) {
    case 'short':
      shell(r * 1.05, -180, 180, 0, 38);
      shell(r * 1.05, 60, 300, 38, 112);
      break;
    case 'long':
      shell(r * 1.07, -180, 180, 0, 38);
      shell(r * 1.07, 55, 305, 38, 135);
      shell(r * 1.06, -28, 28, 36, 47);
      break;
    case 'receding':
      shell(r * 1.04, -180, 180, 0, 28);
      shell(r * 1.04, 60, 300, 28, 112);
      break;
    case 'bald':
      shell(r * 1.03, 45, 315, 70, 108);
      break;
    case 'buzz':
    case 'capped':
      break;
  }

  return head;
}

/* ---------------------------------------------------------------- face --- */

/* -------------------------------------------------------------- hannu --- */

export interface HannuSpec {
  /** The flat `hannu` material the body has always been drawn in. */
  skin: THREE.Material;
  /** Its colour, which is what the face is painted from. */
  skinHex: number;
  hair: THREE.Material;
  hairHex: number;
  /** The tuoppi in his hand: the pour, the head on it, and the glass. */
  beer: THREE.Material;
  foam: THREE.Material;
  glass: THREE.Material;
  rng: Rng;
}

/**
 * How far forward the arm with the tuoppi in it hangs, in radians about the
 * shoulder. It is set once and never touched again; `World` swings the other
 * arm, and this one holds still whatever he is doing, because a man who has
 * been in that water since last night is not the man who spills his beer.
 *
 * The angle is chosen for where it puts the glass rather than for where it
 * puts the hand: upright, out in front of him, and high enough that when he is
 * sat in the palju with the water across his shoulders the beer is still above
 * the surface. A tuoppi under the water is a man who has dropped it.
 */
const TUOPPI_ARM = -1.25;

/**
 * The tuoppi in his hand, standing on the palm.
 *
 * Drawn as the stack `tuoppi()` in `src/config/level.ts` draws every other
 * glass in the house as, off the same dimensions: the pour, and the head on
 * top of it. His is a full one, which is the two-part glass that file has
 * always had - and it is full because there are thirty empties on the tables
 * indoors and he is the one man in the level still enjoying himself.
 *
 * The handle is his alone, and it is the only glass drawn on it. Every other
 * tuoppi here is looked at from across a room, where a handle is four pixels;
 * this one is looked at from the arm's length he catches you at, where a
 * handle is the difference between a tuoppi and a jar of something.
 *
 * The hand is a flat paddle across the end of the forearm, so the glass is
 * built along the palm's normal - the elbow's +Z - and it is `TUOPPI_ARM` that
 * decides that this points up.
 */
function buildTuoppi(hand: THREE.Object3D, spec: HannuSpec): void {
  const mug = new THREE.Group();
  mug.name = 'hannun-tuoppi';
  mug.position.set(0, -0.36, 0.03);
  mug.rotation.x = Math.PI / 2;
  hand.add(mug);

  const stack = (material: THREE.Material, radius: number, height: number, base: number) => {
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 10), material);
    mesh.position.y = base + height / 2;
    mug.add(mesh);
  };
  stack(spec.beer, TUOPPI.radius, TUOPPI.height, 0);
  stack(spec.foam, TUOPPI.wall, TUOPPI.foam, TUOPPI.height);

  // Outboard, so his fist is between it and his belly rather than over it.
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.032, 0.085, 0.02), spec.glass);
  handle.position.set(TUOPPI.wall + 0.014, TUOPPI.height * 0.45, 0);
  mug.add(handle);
}

/**
 * Hannu, standing, feet at the origin. His simulation faces -Z, the way the
 * player's yaw does, so the body is built facing +Z like every head here and
 * turned round once.
 *
 * A big man: the belly, the width across the shoulders and the short neck
 * are all his. The arms hang from groups named `hannu-kasi-oikea` and
 * `hannu-kasi-vasen` with their pivots at the shoulder, so that `World` can
 * swing the right one by setting `rotation.x` - negative is forward, toward
 * whoever he is after. The left one is posed here and never moves again: it
 * has his tuoppi in it, and see `TUOPPI_ARM` for why that settles it.
 */
export function buildHannuFigure(spec: HannuSpec): THREE.Group {
  const skin = spec.skin;
  const group = new THREE.Group();
  const body = new THREE.Group();
  body.rotation.y = Math.PI;
  group.add(body);

  const capsule = (parent: THREE.Object3D, radius: number, length: number, x: number, y: number, z: number) => {
    const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(radius, length, 4, 10), skin);
    mesh.position.set(x, y, z);
    parent.add(mesh);
    return mesh;
  };
  const ball = (parent: THREE.Object3D, rx: number, ry: number, rz: number, x: number, y: number, z: number) => {
    const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 14, 10), skin);
    mesh.scale.set(rx, ry, rz);
    mesh.position.set(x, y, z);
    parent.add(mesh);
    return mesh;
  };
  const box = (parent: THREE.Object3D, w: number, h: number, d: number, x: number, y: number, z: number) => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), skin);
    mesh.position.set(x, y, z);
    parent.add(mesh);
    return mesh;
  };

  // Legs: shin, knee, thigh, and a bare foot - he came out of a tub.
  for (const side of [-1, 1]) {
    capsule(body, 0.075, 0.3, side * 0.13, 0.3, 0);
    ball(body, 0.085, 0.085, 0.085, side * 0.13, 0.52, 0.01);
    capsule(body, 0.1, 0.28, side * 0.135, 0.73, 0);
    box(body, 0.12, 0.08, 0.27, side * 0.14, 0.04, 0.06);
  }

  // Pelvis, the belly over it, and the chest tapering up to the shoulders.
  ball(body, 0.21, 0.14, 0.15, 0, 0.98, 0);
  ball(body, 0.225, 0.17, 0.19, 0, 1.15, 0.03);
  const chest = new THREE.Mesh(new THREE.CylinderGeometry(0.235, 0.205, 0.36, 14), skin);
  chest.scale.z = 0.66;
  chest.position.y = 1.36;
  body.add(chest);
  // The trapezius across the top, so the chest does not end in a flat lid,
  // and a deltoid at each corner of it.
  ball(body, 0.26, 0.1, 0.15, 0, 1.51, 0);
  for (const side of [-1, 1]) ball(body, 0.088, 0.088, 0.088, side * 0.265, 1.49, 0);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.066, 0.074, 0.14, 10), skin);
  neck.position.y = 1.59;
  body.add(neck);

  // A shade larger than life. He is looked at from across a yard as often as
  // from arm's length, and the face is what has to carry at both.
  const head = buildHead({
    radius: 0.135,
    skin: spec.skinHex,
    hair: spec.hairHex,
    style: 'receding',
    expression: 'delighted',
    skinMaterial: skin,
    hairMaterial: spec.hair,
    rng: spec.rng,
    moustache: true,
    stubble: true,
    peeled: true,
  });
  head.position.y = 1.74;
  body.add(head);

  // Arms. The upper arm hangs from the shoulder; the forearm is bent at the
  // elbow and stays bent, so a raised arm is a reach and not a salute.
  for (const side of [-1, 1]) {
    const arm = new THREE.Group();
    // The body is turned round, so its +X is his left.
    arm.name = side > 0 ? 'hannu-kasi-vasen' : 'hannu-kasi-oikea';
    arm.position.set(side * 0.29, 1.5, 0);
    body.add(arm);
    capsule(arm, 0.062, 0.2, 0, -0.16, 0);

    const elbow = new THREE.Group();
    elbow.position.y = -0.31;
    elbow.rotation.x = -0.85;
    arm.add(elbow);
    ball(elbow, 0.066, 0.066, 0.066, 0, 0, 0);
    capsule(elbow, 0.052, 0.18, 0, -0.14, 0);
    box(elbow, 0.085, 0.16, 0.045, 0, -0.35, 0);
    box(elbow, 0.03, 0.07, 0.035, side * 0.055, -0.31, 0.01);

    if (side > 0) {
      arm.rotation.x = TUOPPI_ARM;
      buildTuoppi(elbow, spec);
    }
  }

  // Peeled sheets of skin hanging off him: off the belly, off a shoulder
  // blade, off a hip. Narrow strips, not panels - a panel at the hip is a
  // loincloth, and he is not wearing one.
  for (const [dx, y, dz, h] of [
    [0.16, 1.2, 0.2, 0.3],
    [-0.19, 1.42, -0.12, 0.26],
    [-0.14, 1.0, 0.13, 0.22],
  ] as [number, number, number, number][]) {
    const flap = box(body, 0.12, h, 0.03, dx, y - h / 2, dz);
    flap.rotation.z = dx * 0.9;
    flap.rotation.y = dz < 0 ? Math.PI : 0;
  }

  return group;
}

/* ------------------------------------------------------------- helpers --- */

/**
 * A unit cylinder along Z, for limbs: `scale` is then (width, height,
 * length), the same reading as a unit box's, and a leg stops being a plank.
 */
let limb: THREE.BufferGeometry | null = null;
export function limbGeometry(): THREE.BufferGeometry {
  if (!limb) limb = new THREE.CylinderGeometry(0.5, 0.5, 1, 10).rotateX(Math.PI / 2);
  return limb;
}

/**
 * A unit sphere, read the same way: `scale` is the thing's full width, height
 * and depth. For the parts of a body that are neither planks nor tubes - which
 * on a blockout figure is exactly one pair of them.
 */
let ball: THREE.BufferGeometry | null = null;

export function ballGeometry(): THREE.BufferGeometry {
  if (!ball) ball = new THREE.SphereGeometry(0.5, 14, 10);
  return ball;
}

export function css(hex: number, mul = 1): string {
  const [r, g, b] = rgb(hex).map((c) => Math.max(0, Math.min(255, Math.round(c * mul))));
  return `rgb(${r}, ${g}, ${b})`;
}

